import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Loader, CheckCircle } from 'lucide-react';

const RouterStep3Connect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const router = location.state?.router || location.state?.pendingRouter;
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [routerName, setRouterName] = useState('');
  const [saving, setSaving] = useState(false);

  if (!router) {
    navigate('/routers');
    return null;
  }

  const handleConnect = async () => {
    try {
      setConnecting(true);
      
      const response = await api.post('/api/routers/test-connection', {
        name: `Router ${router.router_index}`,
        ip_address: router.internal_ip || router.vpn_internal_ip,
        username: 'ETWEB',
        password: router.vpn_password,
        port: 8728
      });
      
      if (response.data.api_responds) {
        setConnected(true);
        alert('✅ Conexión exitosa con el router');
      } else {
        alert('❌ No se pudo conectar al router. Verifica que la VPN esté conectada.');
      }
      
    } catch (error) {
      console.error('Error conectando:', error);
      alert(error.response?.data?.detail || 'Error al conectar con el router');
    } finally {
      setConnecting(false);
    }
  };

  const handleSave = async () => {
    if (!routerName || routerName.trim() === '') {
      alert('Por favor ingresa un nombre para el router');
      return;
    }

    try {
      setSaving(true);
      
      await api.post('/api/routers', {
        name: routerName.trim(),
        ip_address: router.internal_ip || router.vpn_internal_ip,
        username: 'ETWEB',
        password: router.vpn_password,
        port: 8728
      });
      
      alert(`✅ Router "${routerName}" configurado exitosamente`);
      navigate('/routers');
      
    } catch (error) {
      console.error('Error guardando router:', error);
      alert(error.response?.data?.detail || 'Error al guardar el router');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/router-step1-script', { 
      state: { 
        router: router
      } 
    });
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            📡 Configurar Router #{router.router_index}
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Paso 2 de 2: Conectar y Nombrar Router
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-green-600 rounded-full"></div>
            <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-green-600 font-semibold">✓ Script</span>
            <span className="text-blue-600 font-semibold">Conectar</span>
          </div>
        </div>

        {/* Datos de conexión */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🔌 Datos de Conexión al Router
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>IP del Router:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.internal_ip || router.vpn_internal_ip}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Puerto API:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>8728</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Usuario:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>ETWEB</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Contraseña:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.vpn_password}</p>
            </div>
          </div>

          {!connected ? (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Conectando...
                </>
              ) : (
                'Conectar al Router'
              )}
            </button>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <span className="text-green-800 font-semibold">✅ Conexión exitosa con el router</span>
            </div>
          )}
        </div>

        {/* Nombre del router (solo si ya conectó) */}
        {connected && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              🏷️ Nombrar Router
            </h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Asigna un nombre identificador a este router (ej: "Sucursal Centro", "Router Principal", etc.)
            </p>
            <input
              type="text"
              value={routerName}
              onChange={(e) => setRouterName(e.target.value)}
              placeholder="Nombre del router"
              className="w-full px-4 py-3 border-2 border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-600 bg-white text-gray-900 placeholder-gray-500"
            />
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-4">
          <button
            onClick={handleBack}
            disabled={connecting || saving}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            } disabled:opacity-50`}
          >
            <ArrowLeft className="w-5 h-5 inline mr-2" />
            Atrás
          </button>
          
          {connected && (
            <button
              onClick={handleSave}
              disabled={saving || !routerName.trim()}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                !routerName.trim()
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Guardar Router
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouterStep3Connect;
